import {
  isPayrollStatementDeliveryFailed,
  isPayrollStatementDeliveryInProgress,
  PAYROLL_STATEMENT_DELIVERY_STATUS,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/delivery-status-rules";
import type {
  PayrollStatementNotificationDelivery,
  PayrollStatementNotificationRecipient,
  PayrollStatementNotificationState,
  SubmittedPayrollStatementNote,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/types";

const getDeliveryId = (
  delivery: PayrollStatementNotificationDelivery | null | undefined,
) => delivery?.id ?? delivery?.delivery_id;

export const getFailedPayrollStatementDeliveries = (
  deliveries: PayrollStatementNotificationDelivery[],
) =>
  deliveries.filter((delivery) =>
    isPayrollStatementDeliveryFailed(delivery.status),
  );

export const mergePayrollStatementDeliveryStatuses = (
  state: PayrollStatementNotificationState | undefined,
  deliveries: PayrollStatementNotificationDelivery[],
) => {
  if (!state || deliveries.length === 0) return state;

  const byTechnicianId = new Map(
    deliveries.flatMap((delivery) =>
      delivery.technician_id
        ? [[delivery.technician_id, delivery] as const]
        : [],
    ),
  );

  return {
    ...state,
    recipients: state.recipients.map((recipient) => {
      const update = byTechnicianId.get(recipient.technician_id);
      if (!update) return recipient;

      const updateId = getDeliveryId(update);
      const existing = updateId
        ? recipient.delivery_history.find(
            (delivery) => getDeliveryId(delivery) === updateId,
          )
        : undefined;
      const merged = {
        ...existing,
        ...update,
        ...(updateId ? { id: updateId } : {}),
      };
      const deliveryHistory = existing
        ? recipient.delivery_history.map((delivery) =>
            getDeliveryId(delivery) === updateId ? merged : delivery,
          )
        : [{ ...merged, note: recipient.note }, ...recipient.delivery_history];

      return {
        ...recipient,
        latest_delivery: merged,
        delivery_history: deliveryHistory,
      };
    }),
  };
};

export const getDeliveryTimestamp = (
  delivery: PayrollStatementNotificationDelivery | null,
) => delivery?.sent_at ?? delivery?.created_at ?? null;

export const reconcileSubmittedNoteDrafts = ({
  noteDrafts,
  submittedNotes,
  recipients,
}: {
  noteDrafts: Record<string, string>;
  submittedNotes: Record<string, SubmittedPayrollStatementNote>;
  recipients: PayrollStatementNotificationRecipient[];
}) => {
  let nextNoteDrafts = noteDrafts;
  let nextSubmittedNotes = submittedNotes;

  Object.entries(submittedNotes).forEach(([technicianId, submittedNote]) => {
    const recipient = recipients.find(
      ({ technician_id }) => technician_id === technicianId,
    );
    const latestDelivery = recipient?.latest_delivery;
    const latestDeliveryId = getDeliveryId(latestDelivery);

    if (
      !latestDelivery ||
      latestDeliveryId !== submittedNote.deliveryId ||
      isPayrollStatementDeliveryInProgress(latestDelivery.status)
    ) {
      return;
    }

    nextSubmittedNotes = { ...nextSubmittedNotes };
    delete nextSubmittedNotes[technicianId];

    if (
      latestDelivery.status === PAYROLL_STATEMENT_DELIVERY_STATUS.succeeded &&
      noteDrafts[technicianId] === submittedNote.note
    ) {
      nextNoteDrafts = { ...nextNoteDrafts };
      delete nextNoteDrafts[technicianId];
    }
  });

  return { noteDrafts: nextNoteDrafts, submittedNotes: nextSubmittedNotes };
};

export const trackSubmittedNoteDrafts = ({
  submittedNotes,
  deliveries,
  noteDrafts,
}: {
  submittedNotes: Record<string, SubmittedPayrollStatementNote>;
  deliveries: PayrollStatementNotificationDelivery[];
  noteDrafts: Record<string, string>;
}) => {
  const next = { ...submittedNotes };

  deliveries.forEach((delivery) => {
    const technicianId = delivery.technician_id;
    const deliveryId = getDeliveryId(delivery);
    const note = technicianId ? noteDrafts[technicianId] : undefined;

    if (
      delivery.status === PAYROLL_STATEMENT_DELIVERY_STATUS.enqueued &&
      technicianId &&
      deliveryId &&
      note !== undefined
    ) {
      next[technicianId] = { deliveryId, note };
    }
  });

  return next;
};
